import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { fetchArticleHistory, searchComponentRows } from "../api/componentsApi";
import DetailOrders from "../component/DetailOrders";
import DetailOffer from "../component/DetailOffer";
import styles from "../Modal.module.css";

const emptyResults = { orders: [], offers: [] };
const emptyOrderFilters = { date: "", file: "", row: "", component: "", qty: "" };
const emptyOfferFilters = { ...emptyOrderFilters, price: "" };

export default function ComponentRowsSearchModal({ isOpen, onClose, embedded = false, partNumber = "" }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(emptyResults);
    const [serverHistory, setServerHistory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState("");
    const [orderFilters, setOrderFilters] = useState(emptyOrderFilters);
    const [offerFilters, setOfferFilters] = useState(emptyOfferFilters);
    const [detail, setDetail] = useState({ type: null, id: null, name: "" });
    const [activeView, setActiveView] = useState("rows");
    const [selectedDynamicComponent, setSelectedDynamicComponent] = useState("");

    const tableResults = useMemo(() => ({
        orders: applyFilters(results.orders, orderFilters, row => ({
            date: formatDate(row.fileOrder?.created),
            file: `${row.fileOrder?.name || ""} ${row.fileOrder?.employeeName || ""}`,
            row: row.rowNumber,
            component: `${row.name || ""} ${row.wrk || ""}`,
            qty: row.cntInt ?? row.cnt
        })),
        offers: applyFilters(results.offers, offerFilters, row => ({
            date: formatDate(row.fileOffer?.created),
            file: `${row.fileOffer?.name || ""} ${row.fileOffer?.employeeName || ""}`,
            row: row.rowNumber,
            component: `${row.name || ""} ${row.wrk || ""}`,
            qty: row.cnt,
            price: `${row.price ?? ""} ${row.dc || ""} ${row.lt || ""}`
        }))
    }), [results, offerFilters, orderFilters]);

    const dynamicComponents = useMemo(() => partNumber ? [partNumber] : Array.from(new Set([
        ...results.orders.map(row => row.name),
        ...results.offers.map(row => row.name)
    ].filter(Boolean))).sort((a, b) => a.localeCompare(b, "ru", { sensitivity: "base" })), [results, partNumber]);

    useEffect(() => {
        if (!dynamicComponents.length) {
            setSelectedDynamicComponent("");
            return;
        }

        if (!dynamicComponents.includes(selectedDynamicComponent)) {
            setSelectedDynamicComponent(dynamicComponents[0]);
        }
    }, [dynamicComponents, selectedDynamicComponent]);

    const selectedComponentRows = useMemo(() => partNumber ? results : ({
        orders: results.orders.filter(row => row.name === selectedDynamicComponent),
        offers: results.offers.filter(row => row.name === selectedDynamicComponent)
    }), [results, selectedDynamicComponent, partNumber]);

    const quantityDynamics = useMemo(() => partNumber ? serverHistory?.quantityDynamics || [] : buildDynamics(
        selectedComponentRows.orders,
        row => row.fileOrder?.created,
        row => Number(row.cntInt ?? row.cnt) || 0,
        "sum"
    ), [selectedComponentRows.orders, partNumber, serverHistory]);

    const priceDynamics = useMemo(() => partNumber ? serverHistory?.priceDynamics || [] : buildPriceDynamics(
        selectedComponentRows.offers,
        row => row.fileOffer?.created,
        row => row.price === null || row.price === undefined || row.price === "" ? Number.NaN : Number(row.price)
    ), [selectedComponentRows.offers, partNumber, serverHistory]);

    const latestActivity = useMemo(() => partNumber ? {
        request: describeTimestamp(serverHistory?.latestRequest), offer: describeTimestamp(serverHistory?.latestOffer)
    } : ({
        request: describeLatestActivity(selectedComponentRows.orders, row => row.fileOrder?.created),
        offer: describeLatestActivity(selectedComponentRows.offers, row => row.fileOffer?.created)
    }), [selectedComponentRows, partNumber, serverHistory]);

    useEffect(() => {
        if (!partNumber || (!embedded && !isOpen)) return undefined;
        let active = true;
        setLoading(true); setHasSearched(true); setError(""); setResults(emptyResults);
        setActiveView("rows"); setOrderFilters(emptyOrderFilters); setOfferFilters(emptyOfferFilters);
        setServerHistory(null);
        fetchArticleHistory(partNumber).then(data => {
            if (!active) return;
            setServerHistory(data);
            setResults({ orders: data.orders, offers: data.offers });
        }).catch(() => { if (active) setError("Не удалось загрузить историю запросов и предложений."); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [partNumber, embedded, isOpen]);

    if (!embedded && !isOpen) return null;

    const search = async () => {
        const normalizedQuery = query.trim();
        if (!normalizedQuery || loading) return;

        setLoading(true);
        setHasSearched(true);
        setError("");
        setOrderFilters(emptyOrderFilters);
        setOfferFilters(emptyOfferFilters);
        setActiveView("rows");
        setSelectedDynamicComponent("");
        setResults(emptyResults);

        try {
            const data = await searchComponentRows(normalizedQuery);
            setResults({
                orders: Array.isArray(data?.orders) ? data.orders : [],
                offers: Array.isArray(data?.offers) ? data.offers : []
            });
        } catch (requestError) {
            console.error("Ошибка поиска строк компонента:", requestError);
            setError("Не удалось выполнить поиск строк. Проверьте работу сервера.");
        } finally {
            setLoading(false);
        }
    };

    const handleQueryChange = event => {
        setQuery(event.target.value);
        setHasSearched(false);
        setError("");
        setOrderFilters(emptyOrderFilters);
        setOfferFilters(emptyOfferFilters);
        setActiveView("rows");
        setSelectedDynamicComponent("");
        setResults(emptyResults);
    };

    const total = results.orders.length + results.offers.length;

    const openDetail = (type, file, rowId) => {
        if (!file?.id) return;
        setDetail({ type, id: file.id, name: file.name || "", rowId });
    };

    const closeDetail = () => setDetail({ type: null, id: null, name: "", rowId: null });

    return (
        <>
        <div className={embedded ? undefined : styles.modal_backdrop} onClick={embedded ? undefined : onClose}>
            <div className={embedded ? styles.rowsSearchEmbedded : `${styles.modal} ${styles.rowsSearchModal}`} onClick={event => event.stopPropagation()}>
                {!embedded && <button type="button" data-modal-close className={styles.closeButton} onClick={onClose} aria-label="Закрыть">×</button>}
                <h2>{partNumber ? `Запросы и предложения — ${partNumber}` : embedded ? "Поиск номенклатуры" : "Поиск компонента"}</h2>
                <p className={styles.rowsSearchHint}>{partNumber ? "Точный артикул, все производители. История сохранённых документов." : "Поиск выполняется без учёта регистра и находит частичное совпадение номера компонента."}</p>

                {!partNumber && <div className={styles.searchBox}>
                    <input
                        className={styles.searchInput}
                        placeholder="Например, ABC-123"
                        aria-label="Артикул для поиска истории"
                        value={query}
                        onChange={handleQueryChange}
                        onKeyDown={event => event.key === "Enter" && search()}
                        autoFocus
                    />
                    <button type="button" className={styles.searchButton} onClick={search} disabled={!query.trim() || loading}>
                        {loading ? "Поиск…" : "Найти строки"}
                    </button>
                </div>}

                {loading && partNumber && <p role="status">Загружаю запросы и предложения…</p>}
                {error && <div className={styles.rowsSearchError}>{error}</div>}
                {hasSearched && !loading && !error && total === 0 && <div className={styles.rowsSearchEmpty}>Строки не найдены.</div>}

                {hasSearched && !loading && !error && total > 0 && (
                    <div className={styles.rowsSearchTabs}>
                        <button type="button" className={activeView === "rows" ? styles.rowsSearchTabActive : ""} onClick={() => setActiveView("rows")}>Найденные строки</button>
                        <button type="button" className={activeView === "dynamics" ? styles.rowsSearchTabActive : ""} onClick={() => setActiveView("dynamics")}>Динамика</button>
                    </div>
                )}

                {hasSearched && !loading && !error && total > 0 && activeView === "rows" && (
                    <div className={styles.rowsSearchGrid}>
                    <RowsSection title="Строки запросов" count={tableResults.orders.length} emptyText="Запросы не найдены">
                        <table className={styles.table}>
                            <thead><tr>
                                <th><FilterHeader label="Дата" value={orderFilters.date} onChange={value => updateFilter(setOrderFilters, "date", value)} /></th>
                                <th className={styles.fileGroupHeader}><FilterHeader label={<GroupHeader variant="file" first="Файл" second="Сотрудник" />} value={orderFilters.file} onChange={value => updateFilter(setOrderFilters, "file", value)} /></th>
                                <th><FilterHeader label="Строка" value={orderFilters.row} onChange={value => updateFilter(setOrderFilters, "row", value)} /></th>
                                <th className={styles.componentGroupHeader}><FilterHeader label={<GroupHeader variant="component" first="Компонент" second="Производитель" />} value={orderFilters.component} onChange={value => updateFilter(setOrderFilters, "component", value)} /></th>
                                <th><FilterHeader label="Количество" value={orderFilters.qty} onChange={value => updateFilter(setOrderFilters, "qty", value)} /></th>
                            </tr></thead>
                            <tbody>{tableResults.orders.map(row => (
                                <tr key={`order-${row.id}`}>
                                    <td>{formatDate(row.fileOrder?.created)}</td>
                                    <td><CellDetails variant="file" primary={row.fileOrder?.name} secondary={row.fileOrder?.employeeName} onPrimaryClick={() => openDetail("order", row.fileOrder, row.id)} /></td>
                                    <td>{row.rowNumber ?? "—"}</td>
                                    <td><CellDetails variant="component" primary={row.name} secondary={row.wrk} /></td>
                                    <td>{row.cntInt ?? row.cnt ?? "—"}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </RowsSection>

                    <RowsSection title="Строки предложений" count={tableResults.offers.length} emptyText="Предложения не найдены">
                        <table className={styles.table}>
                            <thead><tr>
                                <th><FilterHeader label="Дата" value={offerFilters.date} onChange={value => updateFilter(setOfferFilters, "date", value)} /></th>
                                <th className={styles.fileGroupHeader}><FilterHeader label={<GroupHeader variant="file" first="Файл" second="Сотрудник" />} value={offerFilters.file} onChange={value => updateFilter(setOfferFilters, "file", value)} /></th>
                                <th><FilterHeader label="Строка" value={offerFilters.row} onChange={value => updateFilter(setOfferFilters, "row", value)} /></th>
                                <th className={styles.componentGroupHeader}><FilterHeader label={<GroupHeader variant="component" first="Компонент" second="Производитель" />} value={offerFilters.component} onChange={value => updateFilter(setOfferFilters, "component", value)} /></th>
                                <th><FilterHeader label="Количество" value={offerFilters.qty} onChange={value => updateFilter(setOfferFilters, "qty", value)} /></th>
                                <th className={styles.priceGroupHeader}><FilterHeader label={<GroupHeader variant="price" first="Цена" second="DC / LT" />} value={offerFilters.price} onChange={value => updateFilter(setOfferFilters, "price", value)} /></th>
                            </tr></thead>
                            <tbody>{tableResults.offers.map(row => (
                                <tr key={`offer-${row.id}`}>
                                    <td>{formatDate(row.fileOffer?.created)}</td>
                                    <td><CellDetails variant="file" primary={row.fileOffer?.name} secondary={row.fileOffer?.employeeName} onPrimaryClick={() => openDetail("offer", row.fileOffer, row.id)} /></td>
                                    <td>{row.rowNumber ?? "—"}</td>
                                    <td><CellDetails variant="component" primary={row.name} secondary={row.wrk} /></td>
                                    <td>{row.cnt ?? "—"}</td>
                                    <td><CellDetails variant="price" primary={row.price ?? "—"} secondary={`DC: ${row.dc || "—"} · LT: ${row.lt || "—"}`} /></td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </RowsSection>
                    </div>
                )}

                {hasSearched && !loading && !error && total > 0 && activeView === "dynamics" && (
                    <div className={styles.rowsDynamicsView}>
                        <div className={styles.dynamicComponentPicker}>
                            <span>Компонент:</span>
                            <div className={styles.dynamicComponentButtons}>
                                {dynamicComponents.map(component => (
                                    <button
                                        type="button"
                                        key={component}
                                        className={component === selectedDynamicComponent ? styles.dynamicComponentActive : ""}
                                        onClick={() => setSelectedDynamicComponent(component)}
                                    >
                                        {component}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={styles.rowsActivitySummary}>
                            <div>
                                <span>Последний запрос</span>
                                <strong>{latestActivity.request}</strong>
                            </div>
                            <div>
                                <span>Последнее предложение</span>
                                <strong>{latestActivity.offer}</strong>
                            </div>
                        </div>

                        <div className={styles.rowsDynamicsGrid}>
                            <DynamicsChart title={`Запрошенное количество — ${selectedDynamicComponent}`} unit="шт." data={quantityDynamics} color="#2563eb" />
                            <PriceDynamicsChart title={`Цена — ${selectedDynamicComponent}`} data={priceDynamics} />
                        </div>
                    </div>
                )}
            </div>
        </div>

        <DetailOrders
            isOpen={detail.type === "order"}
            selectedOrderFileId={detail.id ? [detail.id] : []}
            numberOffer={detail.name}
            highlightRowId={detail.rowId}
            onClose={closeDetail}
        />

        <DetailOffer
            isOpen={detail.type === "offer"}
            selectedFileIds={detail.id ? [detail.id] : []}
            numberOffer={detail.name}
            highlightRowId={detail.rowId}
            onClose={closeDetail}
        />
        </>
    );
}

function CellDetails({ primary, secondary, variant, onPrimaryClick }) {
    return (
        <div className={`${styles.rowsCellDetails} ${styles[`${variant}Cell`] || ""}`}>
            {onPrimaryClick ? (
                <button type="button" className={`${styles.rowsFileLink} ${styles[`${variant}Primary`]}`} onClick={onPrimaryClick} title="Открыть детализацию документа">
                    {primary || "—"}
                </button>
            ) : (
                <strong className={styles[`${variant}Primary`]}>{primary || "—"}</strong>
            )}
            {secondary && <small className={styles[`${variant}Secondary`]}>{secondary}</small>}
        </div>
    );
}

function GroupHeader({ variant, first, second }) {
    return (
        <span className={styles.groupHeaderLabels}>
            <span className={styles[`${variant}Primary`]}>{first}</span>
            <span className={styles.groupHeaderSeparator}>/</span>
            <span className={styles[`${variant}Secondary`]}>{second}</span>
        </span>
    );
}

function FilterHeader({ label, value, onChange }) {
    return (
        <div className={styles.rowsFilterHeader}>
            <div className={styles.rowsFilterLabel}>{label}</div>
            <input
                value={value}
                onChange={event => onChange(event.target.value)}
                onClick={event => event.stopPropagation()}
                placeholder="Фильтр"
                aria-label={`Фильтр: ${typeof label === "string" ? label : "колонка"}`}
            />
        </div>
    );
}

function updateFilter(setFilters, key, value) {
    setFilters(current => ({ ...current, [key]: value }));
}

function applyFilters(rows, filters, selectValues) {
    const activeFilters = Object.entries(filters).filter(([, value]) => value.trim());
    if (!activeFilters.length) return rows;

    return rows.filter(row => {
        const values = selectValues(row);
        return activeFilters.every(([key, filter]) =>
            String(values[key] ?? "").toLocaleLowerCase("ru").includes(filter.trim().toLocaleLowerCase("ru"))
        );
    });
}

function buildDynamics(rows, getDate, getValue, mode) {
    const grouped = new Map();

    rows.forEach(row => {
        const rawDate = getDate(row);
        const date = rawDate ? new Date(rawDate) : null;
        const value = getValue(row);
        if (!date || Number.isNaN(date.getTime()) || !Number.isFinite(value)) return;

        const key = date.toISOString().slice(0, 10);
        const current = grouped.get(key) || { sum: 0, count: 0 };
        current.sum += value;
        current.count += 1;
        grouped.set(key, current);
    });

    return Array.from(grouped.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
            date,
            value: mode === "average" && value.count ? value.sum / value.count : value.sum
        }));
}

function buildPriceDynamics(rows, getDate, getValue) {
    const grouped = new Map();

    rows.forEach(row => {
        const rawDate = getDate(row);
        const date = rawDate ? new Date(rawDate) : null;
        const value = getValue(row);
        if (!date || Number.isNaN(date.getTime()) || !Number.isFinite(value)) return;

        const key = date.toISOString().slice(0, 10);
        const current = grouped.get(key) || { min: value, max: value };
        current.min = Math.min(current.min, value);
        current.max = Math.max(current.max, value);
        grouped.set(key, current);
    });

    return Array.from(grouped.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, prices]) => ({ date, ...prices }));
}

function describeLatestActivity(rows, getDate) {
    const timestamps = rows
        .map(row => Date.parse(getDate(row) || ""))
        .filter(Number.isFinite);

    if (!timestamps.length) return "Нет данных";

    return describeTimestamp(Math.max(...timestamps));
}

function describeTimestamp(timestamp) {
    const latest = typeof timestamp === "number" ? timestamp : Date.parse(timestamp || "");
    if (!Number.isFinite(latest)) return "Нет данных";
    const days = Math.max(0, Math.floor((Date.now() - latest) / 86400000));
    if (days === 0) return "Сегодня";
    if (days === 1) return "1 день назад";
    if (days >= 2 && days <= 4) return `${days} дня назад`;
    return `${days} дней назад`;
}

function DynamicsChart({ title, unit, data, color }) {
    const option = {
        animationDuration: 450,
        color: [color],
        tooltip: {
            trigger: "axis",
            valueFormatter: value => `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 4 }).format(value)} ${unit}`
        },
        grid: { left: 62, right: 24, top: 32, bottom: 54 },
        xAxis: {
            type: "category",
            data: data.map(item => item.date),
            boundaryGap: false,
            axisLabel: { color: "#64748b", rotate: data.length > 8 ? 35 : 0 }
        },
        yAxis: {
            type: "value",
            name: unit,
            nameTextStyle: { color: "#64748b" },
            axisLabel: { color: "#64748b" },
            splitLine: { lineStyle: { color: "#e8eef6" } }
        },
        series: [{
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 7,
            data: data.map(item => Number(item.value.toFixed(4))),
            lineStyle: { width: 3 },
            areaStyle: { opacity: 0.1 }
        }]
    };

    return (
        <section className={styles.rowsDynamicsCard}>
            <h3>{title}</h3>
            {data.length > 0
                ? <ReactECharts option={option} style={{ height: 360, width: "100%" }} />
                : <div className={styles.rowsDynamicsEmpty}>Недостаточно данных для графика</div>}
        </section>
    );
}

function PriceDynamicsChart({ title, data }) {
    const option = {
        animationDuration: 450,
        color: ["#059669", "#dc2626"],
        tooltip: {
            trigger: "axis",
            valueFormatter: value => `${formatPrice(value)} USD`
        },
        legend: { top: 0, right: 10, textStyle: { color: "#475569" } },
        grid: { left: 62, right: 24, top: 44, bottom: 54 },
        xAxis: {
            type: "category",
            data: data.map(item => item.date),
            boundaryGap: false,
            axisLabel: { color: "#64748b", rotate: data.length > 8 ? 35 : 0 }
        },
        yAxis: {
            type: "value",
            name: "USD",
            scale: true,
            nameTextStyle: { color: "#64748b" },
            axisLabel: {
                color: "#64748b",
                formatter: value => formatPrice(value)
            },
            splitLine: { lineStyle: { color: "#e8eef6" } }
        },
        series: [
            {
                name: "Минимальная цена",
                type: "line",
                smooth: true,
                symbol: "circle",
                symbolSize: 7,
                data: data.map(item => Number(item.min.toFixed(6))),
                lineStyle: { width: 3 }
            },
            {
                name: "Максимальная цена",
                type: "line",
                smooth: true,
                symbol: "diamond",
                symbolSize: 8,
                data: data.map(item => Number(item.max.toFixed(6))),
                lineStyle: { width: 3 }
            }
        ]
    };

    return (
        <section className={styles.rowsDynamicsCard}>
            <h3>{title}</h3>
            {data.length > 0
                ? <ReactECharts option={option} style={{ height: 360, width: "100%" }} />
                : <div className={styles.rowsDynamicsEmpty}>Недостаточно данных для графика</div>}
        </section>
    );
}

function formatPrice(value) {
    return new Intl.NumberFormat("ru-RU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6
    }).format(Number(value) || 0);
}

function RowsSection({ title, count, children, emptyText }) {
    return (
        <section className={styles.rowsSearchSection}>
            <div className={styles.rowsSearchSectionHeader}><h3>{title}</h3><span>{count}</span></div>
            <div className={styles.rowsSearchTable}>{count > 0 ? children : <div className={styles.rowsSearchPaneEmpty}>{emptyText}</div>}</div>
        </section>
    );
}

function formatDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("ru-RU");
}
